import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useImperativeHandle,
    useLayoutEffect,
} from 'react'
import clsx from 'clsx'
import UserLabel from '../../person/UserLabel'
import { createPortal } from 'react-dom'

export type DirectoryUser = {
    id: string
    name: string
    email: string
    avatarUrl?: string
    tenantName?: string
    headline?: string
}

export type InviteRow = {
    email: string
    name?: string
    status: 'existing' | 'new' | 'checking' | 'error'
    error?: string
}

type Props = {
    label?: string
    placeholder?: string
    selected: InviteRow[]
    onRemove: (email: string) => void
    directory: DirectoryUser[]
    onPickUser: (user: DirectoryUser) => void
    onTokenize: (emails: string[]) => void
    className?: string
    disabled?: boolean
    excludeEmails?: string[]
    autoFocus?: boolean
}

export type PeoplePickerInputHandle = {
    focus: () => void
}

const isIOS =
    typeof navigator !== 'undefined' &&
    (() => {
        const ua = navigator.userAgent || ''
        const iOS = /iPad|iPhone|iPod/.test(ua)
        const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
        return iOS || iPadOS
    })()

const PeoplePickerInput = React.forwardRef<PeoplePickerInputHandle, Props>(
    function PeoplePickerInput(
        {
            label,
            placeholder = 'Type a name or email, press Enter',
            selected,
            onRemove,
            directory,
            onPickUser,
            onTokenize,
            className,
            disabled = false,
            excludeEmails = [],
            autoFocus = false,
        }: Props,
        ref,
    ): React.JSX.Element {
        const [raw, setRaw] = useState('')
        const [open, setOpen] = useState(false)
        const inputRef = useRef<HTMLInputElement | null>(null)
        const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
        const [highlightIndex, setHighlightIndex] = useState<number>(-1)
        const containerRef = useRef<HTMLDivElement | null>(null)
        const dropdownRef = useRef<HTMLDivElement | null>(null)
        const [dropdownPos, setDropdownPos] = useState<{
            left: number
            width: number
            top?: number
            bottom?: number
            maxHeight: number
            placement: 'above' | 'below'
        } | null>(null)

        useImperativeHandle(ref, () => ({
            focus: () => inputRef.current?.focus({ preventScroll: true }),
        }))

        // Autofocus on mount if requested - delay on iOS to avoid zoom during modal scale animation.
        useEffect(() => {
            if (!autoFocus || disabled) return
            const delay = isIOS ? 300 : 0 // match your modal open animation duration if you have one
            const id = window.setTimeout(() => {
                inputRef.current?.focus({ preventScroll: true })
            }, delay)
            return () => clearTimeout(id)
        }, [autoFocus, disabled])

        // Filter suggestions to include only those with a non-empty name or email
        const suggestions = useMemo(() => {
            const q = raw.trim().toLowerCase()
            const exclude = new Set([
                ...selected.map((s) => s.email.toLowerCase()),
                ...excludeEmails.map((e) => e.toLowerCase()),
            ])
            const filtered = directory.filter(
                (u) =>
                    !exclude.has(u.email.toLowerCase()) &&
                    (q.length === 0 ||
                        u.name.toLowerCase().includes(q) ||
                        u.email.toLowerCase().includes(q)),
            )
            return filtered
        }, [directory, raw, selected, excludeEmails])

        useEffect(() => {
            if (disabled) setOpen(false)
        }, [disabled])

        // Keep highlight in bounds and reset when suggestions change or menu closes
        useEffect(() => {
            if (!open || suggestions.length === 0) {
                setHighlightIndex(-1)
                return
            }
            setHighlightIndex((h) =>
                h >= suggestions.length ? suggestions.length - 1 : h,
            )
        }, [open, suggestions.length])

        // Scroll highlighted item into view
        useEffect(() => {
            if (highlightIndex >= 0) {
                const el = itemRefs.current[highlightIndex]
                el?.scrollIntoView({ block: 'nearest' })
            }
        }, [highlightIndex, suggestions])

        // Positioning logic for portal dropdown
        useLayoutEffect(() => {
            if (!open) {
                setDropdownPos(null)
                return
            }
            function compute() {
                const anchor = containerRef.current
                if (!anchor) return
                const rect = anchor.getBoundingClientRect()
                const margin = 6
                const vw = window.innerWidth
                const vh = window.innerHeight
                const spaceBelow = vh - rect.bottom - margin
                const spaceAbove = rect.top - margin
                const cap = 320 // px
                const placement: 'above' | 'below' =
                    spaceBelow >= 140 || spaceBelow >= spaceAbove
                        ? 'below'
                        : 'above'
                const available = Math.max(
                    80,
                    Math.min(
                        cap,
                        placement === 'below' ? spaceBelow : spaceAbove,
                    ),
                )
                let left = rect.left
                const width = rect.width
                // Prevent horizontal overflow
                if (left + width > vw - 4) {
                    left = Math.max(4, vw - 4 - width)
                }
                setDropdownPos({
                    left,
                    width,
                    placement,
                    maxHeight: available,
                    top:
                        placement === 'below'
                            ? rect.bottom + margin
                            : undefined,
                    bottom:
                        placement === 'above'
                            ? window.innerHeight - rect.top + margin
                            : undefined,
                })
            }
            compute()
            window.addEventListener('resize', compute, { passive: true })
            window.addEventListener('scroll', compute, { passive: true })
            return () => {
                window.removeEventListener('resize', compute)
                window.removeEventListener('scroll', compute)
            }
        }, [open, suggestions.length, raw])

        // Outside click / escape handlers (close only dropdown)
        useEffect(() => {
            if (!open) return
            function handlePointer(e: MouseEvent) {
                const target = e.target as Node
                if (
                    containerRef.current?.contains(target) ||
                    dropdownRef.current?.contains(target)
                )
                    return
                setOpen(false)
            }
            function handleKey(e: KeyboardEvent) {
                if (e.key === 'Escape') {
                    setOpen(false)
                    setHighlightIndex(-1)
                }
            }
            window.addEventListener('mousedown', handlePointer)
            window.addEventListener('keydown', handleKey)
            return () => {
                window.removeEventListener('mousedown', handlePointer)
                window.removeEventListener('keydown', handleKey)
            }
        }, [open])

        function handleTokenizeAndClear(text: string) {
            const tokens = tokenizeEmails(text)
            if (tokens.length === 0) return
            setRaw('')
            onTokenize(tokens)
            setOpen(false)
            inputRef.current?.focus()
        }

        function selectHighlighted() {
            if (
                !open ||
                highlightIndex < 0 ||
                highlightIndex >= suggestions.length
            )
                return
            const u = suggestions[highlightIndex]
            onPickUser(u)
            setRaw('')
            setOpen(false)
            inputRef.current?.focus()
        }

        const showPlaceholder = raw.length === 0 && selected.length === 0
        const rawTrimmed = raw.trim()
        const lower = rawTrimmed.toLowerCase()
        const isEmail = /.+@.+\..+/.test(rawTrimmed)
        const alreadySelected = selected.some(
            (s) => s.email.toLowerCase() === lower,
        )
        const isExcluded = excludeEmails.some((e) => e.toLowerCase() === lower)
        const showAddHint = isEmail && !alreadySelected && !isExcluded

        return (
            <div className={clsx('w-full', className)}>
                {label && (
                    <label className="block text-sm font-bold mb-1 text-[#333]">
                        {label}
                    </label>
                )}
                <div ref={containerRef} className="relative">
                    {/* Autofill decoy inputs to satisfy aggressive password managers */}
                    <div
                        style={{
                            position: 'absolute',
                            width: 0,
                            height: 0,
                            overflow: 'hidden',
                        }}
                        aria-hidden="true"
                    >
                        <input
                            type="text"
                            name="email"
                            autoComplete="username"
                            tabIndex={-1}
                        />
                        <input
                            type="password"
                            name="password"
                            autoComplete="new-password"
                            tabIndex={-1}
                        />
                    </div>
                    <div
                        className={clsx(
                            'rounded-xl border p-2 focus-within:ring-2 focus-within:ring-blue-400 flex flex-wrap items-center gap-2 min-h-[44px] bg-white',
                            disabled && 'opacity-60 pointer-events-none',
                        )}
                        onClick={() => {
                            if (!disabled) setOpen(true)
                        }}
                    >
                        {[...selected].reverse().map((row) => (
                            <InvitePill
                                key={row.email}
                                row={row}
                                onRemove={() => onRemove(row.email)}
                            />
                        ))}
                        <input
                            ref={inputRef}
                            className="flex-1 min-w-[160px] outline-none bg-transparent p-2 text-[16px] md:text-sm leading-6"
                            placeholder={
                                showPlaceholder ? placeholder : undefined
                            }
                            value={raw}
                            disabled={disabled}
                            name="inviteeLookup"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            inputMode="email"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-dashlane="disabled"
                            onFocus={() => {
                                // keep closed until typing or click handled
                            }}
                            onChange={(e) => {
                                setRaw(e.target.value)
                                setHighlightIndex(-1)
                                if (!open) setOpen(true)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault()
                                    if (!open) setOpen(true)
                                    if (suggestions.length > 0) {
                                        setHighlightIndex(
                                            (h) =>
                                                (((h + 1) %
                                                    suggestions.length) +
                                                    suggestions.length) %
                                                suggestions.length,
                                        )
                                    }
                                    return
                                }
                                if (e.key === 'ArrowUp') {
                                    e.preventDefault()
                                    if (suggestions.length > 0) {
                                        setHighlightIndex(
                                            (h) =>
                                                (((h - 1 + suggestions.length) %
                                                    suggestions.length) +
                                                    suggestions.length) %
                                                suggestions.length,
                                        )
                                    }
                                    return
                                }
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                    if (open && highlightIndex >= 0) {
                                        e.preventDefault()
                                        selectHighlighted()
                                        return
                                    }
                                }
                                if (
                                    ['Enter', 'Tab', ',', ' '].includes(e.key)
                                ) {
                                    e.preventDefault()
                                    handleTokenizeAndClear(raw)
                                    return
                                }
                                if (e.key === 'Escape') {
                                    setOpen(false)
                                    setHighlightIndex(-1)
                                    return
                                }
                            }}
                            onPaste={(e) => {
                                const text = e.clipboardData.getData('text')
                                handleTokenizeAndClear(text)
                                e.preventDefault()
                            }}
                            aria-expanded={
                                open && (suggestions.length > 0 || showAddHint)
                            }
                            aria-haspopup="listbox"
                        />
                    </div>
                </div>
                {open && (suggestions.length > 0 || showAddHint) && dropdownPos
                    ? createPortal(
                          <div
                              ref={dropdownRef}
                              className="z-[100] fixed rounded-xl border bg-white shadow overflow-auto focus:outline-none overscroll-contain"
                              role="listbox"
                              style={{
                                  left: dropdownPos.left,
                                  width: dropdownPos.width,
                                  top: dropdownPos.top,
                                  bottom: dropdownPos.bottom,
                                  maxHeight: dropdownPos.maxHeight,
                              }}
                              data-placement={dropdownPos.placement}
                              onWheel={(e) => {
                                  e.stopPropagation()
                              }}
                          >
                              {suggestions.length === 0 && showAddHint ? (
                                  <div className="px-3 py-2 text-sm text-gray-500">
                                      {`Hit enter to add ${rawTrimmed}`}
                                  </div>
                              ) : (
                                  suggestions.map((u, idx) => (
                                      <button
                                          type="button"
                                          key={u.id}
                                          ref={(el) =>
                                              (itemRefs.current[idx] = el)
                                          }
                                          className={clsx(
                                              'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2',
                                              highlightIndex === idx &&
                                                  'bg-gray-100',
                                          )}
                                          role="option"
                                          aria-selected={highlightIndex === idx}
                                          onMouseEnter={() =>
                                              setHighlightIndex(idx)
                                          }
                                          onMouseDown={(e) =>
                                              e.preventDefault()
                                          }
                                          onClick={() => {
                                              onPickUser(u)
                                              setRaw('')
                                              setOpen(false)
                                              inputRef.current?.focus()
                                          }}
                                      >
                                          <div className="flex flex-col gap-1 min-w-0">
                                              <UserLabel
                                                  name={u.name}
                                                  email={u.email}
                                                  avatarUrl={u.avatarUrl}
                                                  size={36}
                                                  className="py-0"
                                              />
                                          </div>
                                      </button>
                                  ))
                              )}
                          </div>,
                          document.body,
                      )
                    : null}
            </div>
        )
    },
)

export default PeoplePickerInput

function InvitePill({
    row,
    onRemove,
}: {
    row: InviteRow
    onRemove: () => void
}): React.JSX.Element {
    return (
        <span
            className={clsx(
                'inline-flex h-7 items-center gap-2 px-2 rounded-full text-xs ring-1 leading-[1] whitespace-nowrap align-middle',
                'ring-emerald-200 bg-emerald-50 text-emerald-800',
            )}
            title={row.email}
        >
            <span className="max-w-[160px] truncate leading-none">
                {row.email}
            </span>
            <button
                className="ml-1 opacity-60 hover:opacity-100 leading-none inline-flex items-center justify-center w-4 h-4 shrink-0"
                onClick={onRemove}
                aria-label={`Remove ${row.email}`}
                type="button"
            >
                ✕
            </button>
        </span>
    )
}

function tokenizeEmails(text: string): string[] {
    if (!text) return []
    const parts = text
        .split(/[\s,;]+/)
        .map((p) => p.trim())
        .filter(Boolean)
    const uniq = Array.from(new Set(parts))
    return uniq.filter((p) => /.+@.+\..+/.test(p))
}
